import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown, Lock, Search } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { DateInput } from '@/components/date-input';
import { Checkbox, ErrorBanner, Field, NativeSelect, Textarea } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { serverFieldErrors } from '@/components/settings-section';
import { VatTabs } from '@/components/vat';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  RETURN_BOXES,
  TAX_KIND_LABELS,
  formatRate,
  useTaxCodes,
  type TaxCode,
} from '@/lib/accounts';
import { ApiError, api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { todayIso } from '@/lib/dates';
import { formatDate } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { isIndia, useOrgLocale } from '@/lib/org-locale';

export const Route = createFileRoute('/taxes/vat/rates')({ component: TaxRatesPage });

const APPLIES: Record<TaxCode['appliesTo'], string> = {
  sales: 'Sales',
  purchases: 'Purchases',
  both: 'Sales and purchases',
};
const ZERO_KINDS = new Set(['zero', 'exempt', 'out_of_scope']);

/** Screen T3 (document 06): Taxes › VAT › Tax rates, list pattern with a drawer. */
function TaxRatesPage() {
  const india = useOrgLocale().taxPack === 'in';
  const orgId = useOrgId();
  const me = useMe();
  const canEdit = me.data?.isStaff ?? false;
  const [includeInactive, setIncludeInactive] = useState(false);
  const [q, setQ] = useState('');
  const [drawer, setDrawer] = useState<{ open: boolean; code: TaxCode | null }>({
    open: false,
    code: null,
  });
  const codes = useTaxCodes({ includeInactive });
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: (t: TaxCode) =>
      api<TaxCode>(`/orgs/${orgId}/tax-codes/${t.id}/${t.isActive ? 'deactivate' : 'reactivate'}`, {
        method: 'POST',
      }),
    onSuccess: async (t) => {
      await qc.invalidateQueries({ queryKey: ['tax-codes', orgId] });
      toast(`${t.code} is ${t.isActive ? 'active again' : 'now inactive'}.`);
    },
    onError: (e) => toast(e.message, { tone: 'error' }),
  });

  const needle = q.trim().toLowerCase();
  const rows = (codes.data ?? []).filter(
    (t) => !needle || `${t.code} ${t.name}`.toLowerCase().includes(needle),
  );

  return (
    <>
      <PageHeader
        title={india ? 'Tax rates' : 'VAT'}
        description={
          india
            ? 'GST codes used on transactions: rate, CGST + SGST or IGST, and the GSTR-1 / GSTR-3B category.'
            : 'UAE VAT codes used on transactions and the VAT 201 box each one reports to.'
        }
        actions={
          canEdit ? (
            <Button onClick={() => setDrawer({ open: true, code: null })}>New tax rate</Button>
          ) : null
        }
      />
      {india ? null : <VatTabs />}
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : (
        <>
          <div className="mb-3 flex items-center gap-4">
            <div className="relative w-[320px]">
              <Search className="pointer-events-none absolute left-2 top-2.5 size-4 text-muted-foreground" />
              <Input
                className="bg-card pl-8"
                placeholder="Filter by code or name"
                aria-label="Filter tax rates"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <Checkbox
              label="Include inactive"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
          </div>
          {codes.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : codes.isError ? (
            <EmptyState title="Could not load tax rates" hint={codes.error.message} />
          ) : rows.length === 0 ? (
            <EmptyState title="No tax rates match your filter" />
          ) : (
            <div className="rounded-lg border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[90px]">Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="w-[80px] text-right">Rate</TableHead>
                    <TableHead>Kind</TableHead>
                    <TableHead>Applies to</TableHead>
                    <TableHead className="w-[100px]">
                      {india ? 'Return category' : 'Return box'}
                    </TableHead>
                    <TableHead className="w-[130px]">Effective from</TableHead>
                    <TableHead className="w-[90px]">Status</TableHead>
                    <TableHead className="w-[140px] text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((t) => (
                    <TableRow key={t.id} className={t.isActive ? '' : 'text-muted-foreground'}>
                      <TableCell className="font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          {t.code}
                          {t.inUse ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Lock
                                  className="size-3.5 text-muted-foreground"
                                  aria-label="Used on transactions"
                                />
                              </TooltipTrigger>
                              <TooltipContent>
                                Used on transactions: the rate and kind are fixed.
                              </TooltipContent>
                            </Tooltip>
                          ) : null}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>{t.name}</div>
                        {t.description ? (
                          <div className="max-w-[380px] truncate text-xs text-muted-foreground">
                            {t.description}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="tnum text-right">{formatRate(t.rate)}</TableCell>
                      <TableCell>{TAX_KIND_LABELS[t.kind]}</TableCell>
                      <TableCell>{APPLIES[t.appliesTo]}</TableCell>
                      <TableCell>
                        {india
                          ? [
                              t.returnBox
                                ? (GST_CATEGORY_LABELS[t.returnBox] ?? t.returnBox)
                                : 'Not reported',
                              t.gstSplit ? GST_SPLIT_LABELS[t.gstSplit] : null,
                            ]
                              .filter(Boolean)
                              .join(' · ')
                          : t.returnBox
                            ? t.returnBox === '1'
                              ? '1a–1g'
                              : t.returnBox
                            : '—'}
                      </TableCell>
                      <TableCell>{formatDate(t.effectiveFrom)}</TableCell>
                      <TableCell>
                        {t.isActive ? (
                          <Badge variant="outline">Active</Badge>
                        ) : (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {canEdit ? (
                          <div className="inline-flex">
                            <Button
                              size="sm"
                              variant="link"
                              className="px-2"
                              onClick={() => setDrawer({ open: true, code: t })}
                            >
                              Edit
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="icon-sm"
                                  variant="ghost"
                                  aria-label={`More actions for ${t.code}`}
                                >
                                  <ChevronDown className="size-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onSelect={() => toggle.mutate(t)}>
                                  {t.isActive ? 'Make inactive' : 'Make active'}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            Inactive codes stay on the transactions that used them and drop out of pickers.
          </p>
        </>
      )}
      <TaxRateDrawer
        open={drawer.open}
        code={drawer.code}
        onOpenChange={(open) => setDrawer((d) => ({ ...d, open }))}
      />
    </>
  );
}

const GST_CATEGORY_LABELS: Record<string, string> = {
  TAX: 'Taxable',
  EXP: 'Export (zero rated)',
  NIL: 'Nil rated',
  EXEMPT: 'Exempt',
  NONGST: 'Non-GST',
  RCM: 'Reverse charge',
};
const GST_SPLIT_LABELS: Record<string, string> = { cgst_sgst: 'CGST + SGST', igst: 'IGST' };

interface Form {
  code: string;
  name: string;
  kind: TaxCode['kind'];
  rate: string;
  appliesTo: TaxCode['appliesTo'];
  returnBox: string;
  gstSplit: string;
  effectiveFrom: string;
  description: string;
}

const blank = (): Form => ({
  code: '',
  name: '',
  kind: 'standard',
  rate: '5',
  appliesTo: 'both',
  returnBox: isIndia() ? 'TAX' : '1',
  gstSplit: isIndia() ? 'cgst_sgst' : '',
  effectiveFrom: todayIso(),
  description: '',
});

function TaxRateDrawer({
  open,
  code,
  onOpenChange,
}: {
  open: boolean;
  code: TaxCode | null;
  onOpenChange: (open: boolean) => void;
}) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const [form, setForm] = useState<Form>(blank());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set =
    <K extends keyof Form>(k: K) =>
    (v: Form[K]) =>
      setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(
      code
        ? {
            code: code.code,
            name: code.name,
            kind: code.kind,
            rate: code.rate,
            appliesTo: code.appliesTo,
            returnBox: code.returnBox ?? '',
            gstSplit: code.gstSplit ?? '',
            effectiveFrom: code.effectiveFrom,
            description: code.description ?? '',
          }
        : blank(),
    );
  }, [open, code]);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: form.code.trim(),
        name: form.name.trim(),
        kind: form.kind,
        rate: form.rate.trim(),
        appliesTo: form.appliesTo,
        returnBox: form.returnBox || null,
        ...(isIndia() ? { gstSplit: form.gstSplit || null } : {}),
        effectiveFrom: form.effectiveFrom,
        description: form.description,
      };
      return code
        ? api<TaxCode>(`/orgs/${orgId}/tax-codes/${code.id}`, {
            method: 'PATCH',
            body: JSON.stringify(body),
          })
        : api<TaxCode>(`/orgs/${orgId}/tax-codes`, { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: async (t) => {
      await qc.invalidateQueries({ queryKey: ['tax-codes', orgId] });
      toast(`Tax rate ${t.code} saved.`);
      onOpenChange(false);
    },
    onError: (e) => setErrors(serverFieldErrors(e)),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!/^[A-Za-z0-9][A-Za-z0-9-]{0,9}$/.test(form.code.trim()))
      next['code'] = 'Up to 10 letters, digits or dashes.';
    if (!form.name.trim()) next['name'] = 'Enter a name.';
    if (!/^\d{1,3}(\.\d{1,2})?$/.test(form.rate.trim()))
      next['rate'] = 'Enter a percentage such as 5 or 5.00.';
    else if (ZERO_KINDS.has(form.kind) && Number(form.rate) !== 0)
      next['rate'] = 'This kind has a 0% rate.';
    else if (!ZERO_KINDS.has(form.kind) && Number(form.rate) === 0)
      next['rate'] = 'This kind needs a rate above 0%.';
    if (!form.effectiveFrom) next['effectiveFrom'] = 'Enter a date.';
    setErrors(next);
    if (!Object.keys(next).length) save.mutate();
  }

  const locked = Boolean(code?.inUse);
  const banner =
    save.error && !(save.error instanceof ApiError && Object.keys(errors).length)
      ? save.error.message
      : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[520px]">
        <form noValidate onSubmit={submit} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle>{code ? `Edit tax rate ${code.code}` : 'New tax rate'}</SheetTitle>
            <SheetDescription>
              {locked
                ? 'Used on transactions: the rate and kind are fixed. Add a new code for a new rate.'
                : 'Rates and VAT 201 boxes are data, so they can be corrected without a release.'}
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            <ErrorBanner>{banner}</ErrorBanner>
            <div className="grid grid-cols-[140px_1fr] gap-3">
              <Field label="Code" error={errors['code']}>
                {(id) => (
                  <Input
                    id={id}
                    value={form.code}
                    autoFocus={!code}
                    aria-invalid={Boolean(errors['code'])}
                    onChange={(e) => set('code')(e.target.value.toUpperCase())}
                  />
                )}
              </Field>
              <Field label="Name" error={errors['name']}>
                {(id) => (
                  <Input
                    id={id}
                    value={form.name}
                    aria-invalid={Boolean(errors['name'])}
                    onChange={(e) => set('name')(e.target.value)}
                  />
                )}
              </Field>
            </div>
            <div className="grid grid-cols-[1fr_140px] gap-3">
              <Field label="Kind">
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={form.kind}
                    disabled={locked}
                    onChange={(e) => {
                      const kind = e.target.value as TaxCode['kind'];
                      setForm((f) => ({
                        ...f,
                        kind,
                        rate: ZERO_KINDS.has(kind) ? '0' : f.rate === '0' ? '5' : f.rate,
                      }));
                    }}
                  >
                    {Object.entries(TAX_KIND_LABELS).map(([k, label]) => (
                      <option key={k} value={k}>
                        {label}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
              <Field label="Rate (%)" error={errors['rate']}>
                {(id) => (
                  <Input
                    id={id}
                    value={form.rate}
                    inputMode="decimal"
                    className="tnum text-right"
                    disabled={locked}
                    aria-invalid={Boolean(errors['rate'])}
                    onChange={(e) => set('rate')(e.target.value)}
                  />
                )}
              </Field>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Applies to">
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={form.appliesTo}
                    onChange={(e) => set('appliesTo')(e.target.value as TaxCode['appliesTo'])}
                  >
                    {Object.entries(APPLIES).map(([k, label]) => (
                      <option key={k} value={k}>
                        {label}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
              <Field label="Effective from" error={errors['effectiveFrom']}>
                {(id) => (
                  <DateInput
                    id={id}
                    value={form.effectiveFrom}
                    invalid={Boolean(errors['effectiveFrom'])}
                    onChange={set('effectiveFrom')}
                  />
                )}
              </Field>
            </div>
            {isIndia() ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Return category" hint="GSTR-1 table and GSTR-3B row.">
                  {(id) => (
                    <NativeSelect
                      id={id}
                      value={form.returnBox}
                      onChange={(e) => set('returnBox')(e.target.value)}
                    >
                      <option value="">Not reported (blocked credit)</option>
                      {Object.entries(GST_CATEGORY_LABELS).map(([v, label]) => (
                        <option key={v} value={v}>
                          {label}
                        </option>
                      ))}
                    </NativeSelect>
                  )}
                </Field>
                <Field label="Tax split" hint="Intra-state: CGST + SGST; inter-state: IGST.">
                  {(id) => (
                    <NativeSelect
                      id={id}
                      value={form.gstSplit}
                      onChange={(e) => set('gstSplit')(e.target.value)}
                    >
                      <option value="">None (nil, exempt, non-GST, exports)</option>
                      {Object.entries(GST_SPLIT_LABELS).map(([v, label]) => (
                        <option key={v} value={v}>
                          {label}
                        </option>
                      ))}
                    </NativeSelect>
                  )}
                </Field>
              </div>
            ) : (
              <Field label="VAT 201 box">
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={form.returnBox}
                    onChange={(e) => set('returnBox')(e.target.value)}
                  >
                    <option value="">Not reported</option>
                    {RETURN_BOXES.map((b) => (
                      <option key={b.value} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            )}
            <Field label="Description">
              {(id) => (
                <Textarea
                  id={id}
                  value={form.description}
                  onChange={(e) => set('description')(e.target.value)}
                />
              )}
            </Field>
          </div>
          <SheetFooter className="flex-row justify-end border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save and close'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
